import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronDown, ChevronRight, X } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'

interface PublicUserStats {
  roads_suggested: number
  pct_accepted: number
  roads_flagged: number
}

const SECTIONS = ['Contributing', 'Riding', 'Social', 'Bikes', 'Placeholder'] as const
type SectionName = (typeof SECTIONS)[number]

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 py-2.5 text-sm">
      <span className="text-white/60">{label}</span>
      <span className="font-medium text-white">{value}</span>
    </div>
  )
}

function Section({
  title,
  open,
  onToggle,
  children,
}: {
  title: SectionName
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <div className="border-b border-white/5">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between py-3 text-left"
        aria-expanded={open}
      >
        <span className="font-mono text-xs uppercase tracking-widest text-white/70">
          {title}
        </span>
        {open ? (
          <ChevronDown size={14} className="text-white/40" />
        ) : (
          <ChevronRight size={14} className="text-white/40" />
        )}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="pb-3">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default function StatsPanel({
  open,
  username,
  onClose,
}: {
  open: boolean
  username: string | null
  onClose: () => void
}) {
  const [openSection, setOpenSection] = useState<SectionName | null>('Contributing')
  const [stats, setStats] = useState<PublicUserStats | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')

  useEffect(() => {
    if (!open || !username) return
    let cancelled = false
    setStatus('loading')
    setStats(null)
    supabase
      .rpc('public_user_stats', { _username: username })
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) {
          setStatus('error')
          return
        }
        const row = (data as PublicUserStats | null) ?? null
        setStats(
          row
            ? {
                roads_suggested: row.roads_suggested ?? 0,
                pct_accepted: row.pct_accepted ?? 0,
                roads_flagged: row.roads_flagged ?? 0,
              }
            : { roads_suggested: 0, pct_accepted: 0, roads_flagged: 0 },
        )
        setStatus('ready')
      })
    return () => {
      cancelled = true
    }
  }, [open, username])

  useEffect(() => {
    if (open) setOpenSection('Contributing')
  }, [open])

  if (!open || !username) return null

  const allZero =
    stats !== null &&
    stats.roads_suggested === 0 &&
    stats.pct_accepted === 0 &&
    stats.roads_flagged === 0

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="stats-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
        className="fixed inset-0 z-[55] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.97 }}
          transition={{ type: 'spring', damping: 28, stiffness: 280 }}
          onClick={(e) => e.stopPropagation()}
          className="flex max-h-[calc(100vh-64px)] w-full max-w-[560px] flex-col rounded-xl border border-white/10 bg-[#0C0C0E]/95 backdrop-blur"
          role="dialog"
          aria-modal="true"
          aria-label="User stats"
        >
          <div className="flex items-start justify-between gap-3 p-4 pb-3">
            <div className="min-w-0">
              <div className="font-mono text-sm uppercase tracking-widest text-amber-400">
                Stats
              </div>
              <div className="mt-1 truncate text-sm font-semibold text-white">{username}</div>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="rounded-lg p-1 text-white/40 hover:bg-white/10 hover:text-white"
            >
              <X size={16} />
            </button>
          </div>

          <div className="overflow-y-auto px-4 pb-10">
            {SECTIONS.map((section) => (
              <Section
                key={section}
                title={section}
                open={openSection === section}
                onToggle={() =>
                  setOpenSection((cur) => (cur === section ? null : section))
                }
              >
                {section === 'Contributing' ? (
                  status === 'loading' ? (
                    <div className="py-2 text-xs text-white/40">Loading…</div>
                  ) : status === 'error' || !stats ? (
                    <div className="py-2 text-xs text-white/40">Could not load stats.</div>
                  ) : (
                    <div>
                      <StatRow label="Roads suggested" value={String(stats.roads_suggested)} />
                      <StatRow label="Percentage accepted" value={`${stats.pct_accepted}%`} />
                      <StatRow label="Roads flagged" value={String(stats.roads_flagged)} />
                      {allZero && (
                        <p className="mt-2 text-xs italic text-white/40">
                          No contributions yet — suggest roads or flag issues to build your
                          stats.
                        </p>
                      )}
                    </div>
                  )
                ) : (
                  <p className="py-2 text-xs italic text-white/40">Coming soon.</p>
                )}
              </Section>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  )
}
